import React from 'react';
import { View } from 'react-native';
import Text from 'design-system/Text';
import Amount from 'design-system/Amount';
import Spacer from 'design-system/Spacer';
import { rem } from 'design-system/values';
import { CardView } from 'design-system/CardView';
import UpgradeCard from 'design-system/UpgradeCard';
import IconCircle from 'design-system/icons/IconCircle';
import IconPieChart from 'design-system/icons/IconPieChart';
import useShouldShowUpgrade from 'features/premium/hooks/useShouldShowUpgrade';
import IconTransactionCategory from 'features/transactions/icons/IconTransactionCategory';
import { selectIsScrambled } from 'hooks';
import { isDefaultCategory, useColorOfCategory } from 'hooks/useCategory';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import { isAfter } from 'date-fns';
import checkFeatureFlag from 'utils/featureFlags';
import Logo from 'design-system/Logo';
import { BudgetingCircleProgressBar } from './BudgetingCircleProgressBar';
import BudgetingGraphLabel from './BudgetingGraphLabel';
import SetupBudgetCard from './SetupBudgetCard';
import { isWeb } from '../../../constants';
import { BudgetRollingAmountLabel } from './BudgetRollingAmountLabel';
import BudgetingMonthlyCategoryToolTip from './BudgetingMonthlyCategoryToolTip';
import BudgetingControls from './BudgetingControls';
import SubCategoriesList from './SubCategoriesList';
const CATEGORY_ICON_SIZE = rem(32);
const isLatest = (endDate) => isAfter(new Date(endDate), new Date());
const MonthlyCategoryHeader = ({ data, periodStartDate, periodEndDate, onPressSetup, type, }) => {
    const colorOfCategory = useColorOfCategory();
    const scrambled = useAppSelector(selectIsScrambled);
    const styles = useStyles(styleSet);
    const colors = useColors();
    const showSubcategoriesCreateFlow = useAppSelector((store) => checkFeatureFlag(store.utils.featureFlags, 'show_subcategories_create_flow'));
    const isParentCategory = !!data.id && 'parentCategoryId' in data && !data.parentCategoryId;
    const benefitId = type === 'merchant' ? 'merchantBudgets' : 'customCategories';
    const { isUnlockedForSpace, onPressUpgrade } = useShouldShowUpgrade({
        benefitId,
    });
    const color = type === 'category'
        ? colorOfCategory(data)
        : colors.categories.custom;
    if (!isUnlockedForSpace &&
        (type === 'merchant' || !isDefaultCategory(data.id.toString()))) {
        return (<>
        <Spacer h={16}/>
        <UpgradeCard title="See your spending" description={`Upgrade your plan to continue budgeting on your ${type === 'merchant' ? 'merchants' : 'custom categories'}`} icon={<IconPieChart />} noMarginBottom onPressUpgrade={onPressUpgrade}/>
      </>);
    }
    /**
     * Merchant budgets should always have a baseLimit but they are
     * still handled here in case they do not
     */
    if (typeof data.baseLimit !== 'number') {
        return (<View>
        <View style={styles.container}>
          <IconCircle width={64} height={64} style={styles.icon} color={colors.cards.onDark}>
            {type === 'category' ? (<IconTransactionCategory category={data} width={CATEGORY_ICON_SIZE} lightBackground height={CATEGORY_ICON_SIZE}/>) : (<Logo R64 uri={data.iconUrl}/>)}
          </IconCircle>
          <Amount Numbers-20 centered value={{ value: Math.abs(data.total), currency: data.currency }} style={styles.amountTitle}/>
          <Text TextThin-14 centered Secondary style={styles.subtitle}>
            {data.total <= 0 ? 'Spending' : 'Income'}
          </Text>
        </View>
        <SetupBudgetCard noMarginBottom onPress={onPressSetup} title="Set up a budget" subTitle="Keep track of your spending"/>
      </View>);
    }
    return (<View>
      <BudgetingControls payPeriod={{ from: periodStartDate, to: periodEndDate }}/>
      <Spacer h={8}/>
      <CardView withPadding noMarginBottom>
        {type === 'category' && !!isParentCategory && (<BudgetRollingAmountLabel defaultBudget={data.baseLimit} currency={data.currency} rollingAccumulatedLimit={data?.rollingAccumulatedLimit} isLatest={isLatest(periodEndDate)}/>)}
        <BudgetingCircleProgressBar totalBudget={data.budgetLimit || 0} spending={data.total} rollingAmount={data.rollingAccumulatedLimit} baseLimit={data.baseLimit} scrambled={scrambled} currency={data.currency} gradientColors={[color, color]}/>
        <Spacer.H24 />
        <BudgetingGraphLabel title="Spending" color={color} amount={{
            currency: data.currency,
            amount: data.total,
        }} style={styles.label}/>
      </CardView>
      {data.rollingAccumulatedLimit && isParentCategory ? (<View style={styles.toolTipContainer}>
          <BudgetingMonthlyCategoryToolTip currency={data.currency} baseLimit={data.baseLimit} totalLimit={data.totalLimit || 0} totalSpending={data.total} rollingAccumulatedLimit={data.rollingAccumulatedLimit} onCard={false}/>
        </View>) : null}
      {!!isParentCategory && !!showSubcategoriesCreateFlow && (<SubCategoriesList parentCategory={data}/>)}
    </View>);
};
const styleSet = createStyleSheets(() => ({
    container: {
        alignItems: 'center',
    },
    icon: {
        marginTop: rem(14),
        marginBottom: rem(22),
    },
    amountTitle: {
        marginBottom: rem(4),
    },
    subtitle: {
        marginBottom: rem(16),
    },
    label: {
        ...(isWeb && {
            paddingVertical: 0,
            paddingHorizontal: 0,
        }),
    },
    toolTipContainer: {
        paddingHorizontal: rem(16),
    },
}));
export default MonthlyCategoryHeader;
